export type canvasOptions = {
	fullScreen?: boolean;
	appendToBody?: boolean;
	width?: number;
	height?: number;
	fitMode?: 'stretch' | 'contain';
};

// See all options here:
// https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/getContext
export type contextOptions = {
	alpha?: boolean;
	desynchronized?: boolean;
	antialias?: boolean;
	depth?: boolean;
	failIfMajorPerformanceCaveat?: boolean;
	powerPreference?: 'default' | 'high-performance' | 'low-power';
	premultipliedAlpha?: boolean;
	preserveDrawingBuffer?: boolean;
	stencil?: boolean;
};

function getWebGLContext (canvas: HTMLCanvasElement, contextOptions: contextOptions) {
	let webGLContext;
	// Testing for WebGL support
	try {
		const options: contextOptions = { ...contextOptions };

		// overriding antialias if not defined, can have huge performance impact on iOS devices 
		if (options.antialias === undefined) { options.antialias = false; }

		webGLContext = canvas.getContext('webgl', options) || canvas.getContext('experimental-webgl', options);
	} catch (error) {
		return null;
	}

	if (!webGLContext) {
		// Failed to create the context but did not even throw
		return null;
	}

	return webGLContext;
}

export class WebGLView {
	canvas: HTMLCanvasElement;
	gl: WebGLRenderingContext;

	isFullScreen: boolean;
	private _setFullScreenWrapper: () => void;
	private _fullScreenFixedSize: { width: number; height: number } | null;
	private _fullScreenFitMode: 'stretch' | 'contain';

	width: number;
	height: number;
	styleWidth: number;
	styleHeight: number;

	private _onResizeCallbacks: Array<(...args: any[]) => any>;

	constructor (canvasOptions: canvasOptions = {}, contextOptions: contextOptions = {}) {
		const canvas = document.createElement('canvas');
		const gl = getWebGLContext(canvas, contextOptions);

		this.canvas = canvas;
		this.gl = gl;
		this.isFullScreen = false;
		this.width = 0;
		this.height = 0;
		this.styleWidth = 0;
		this.styleHeight = 0;
		this._onResizeCallbacks = [];
		this._fullScreenFixedSize = null;
		this._fullScreenFitMode = 'stretch';

		this._setFullScreenWrapper = () => {
			this._setFullScreen();
		};

		const fullScreen = canvasOptions.fullScreen;
		const width = canvasOptions.width;
		const height = canvasOptions.height;
		this._fullScreenFitMode = canvasOptions.fitMode ?? 'stretch';
		if (typeof width === 'number' && typeof height === 'number') {
			this._fullScreenFixedSize = { width, height };
		}
		canvas.style.position = 'absolute';

		if ((fullScreen === true) || (fullScreen === undefined && width === undefined && height === undefined)) {
			this.setFullScreen();
		} else {
			this._resize(width || 800, height || 600);
			this.canvas.style.left = '0px';
			this.canvas.style.top  = '0px';

			const appendToBody = canvasOptions.appendToBody;
			if (appendToBody === undefined) {	
				this.appendToDom(document.body);
			}
		}
	}

	show () {
		this.canvas.style.display = '';
	}

	hide () {
		this.canvas.style.display = 'none';
	}

	appendToDom (domElement: HTMLElement) {
		domElement.appendChild(this.canvas);
	}

	_resize (width: number, height: number, styleWidth: number = width, styleHeight: number = height) {
		this.width = width;
		this.height = height;

		// Resizing canvas
		this.canvas.width  = width;
		this.canvas.height = height;

		this.styleWidth  = styleWidth;
		this.styleHeight = styleHeight;

		this.canvas.style.width  = this.styleWidth.toString()  + 'px';
		this.canvas.style.height = this.styleHeight.toString() + 'px';

		// Hack to make sure the top of the canvas matches with the top of the inner window
		window.scrollTo(0, 1);

		// Triggering callbacks
		for (var c = 0; c < this._onResizeCallbacks.length; c += 1) {
			this._onResizeCallbacks[c](width, height);
		}
	}

	resize (width: number, height: number, styleWidth?: number, styleHeight?: number) {
		if (this.isFullScreen) {
			this.isFullScreen = false;
			window.removeEventListener('resize', this._setFullScreenWrapper);
		}

		this._resize(width, height, styleWidth, styleHeight);
	}

	_setFullScreen () {
		if (this._fullScreenFitMode === 'contain' && this._fullScreenFixedSize) {
			const targetWidth = this._fullScreenFixedSize.width;
			const targetHeight = this._fullScreenFixedSize.height;
			const scale = Math.min(window.innerWidth / targetWidth, window.innerHeight / targetHeight);
			const styleWidth = Math.max(1, Math.round(targetWidth * scale));
			const styleHeight = Math.max(1, Math.round(targetHeight * scale));

			this._resize(targetWidth, targetHeight, styleWidth, styleHeight);
			this._setCanvasPosition(styleWidth, styleHeight);
			this._applyFullScreenStyles();
			return;
		}

		let viewWidth: number, viewHeight: number;
		if ((1 - window.innerWidth / window.innerHeight) * (1 - screen.width / screen.height) < 0) {
			if (window.innerWidth < screen.height) {
				// Desktop
				viewWidth  = window.innerWidth;
				viewHeight = window.innerHeight;
			} else {
				// Mobile
				viewWidth  = screen.height;
				viewHeight = Math.round(screen.height * window.innerHeight / window.innerWidth);
			}
		} else {
			if (window.innerWidth < screen.width) {
				// Desktop
				viewWidth  = window.innerWidth;
				viewHeight = window.innerHeight;
			} else {
				// Mobile
				viewWidth  = screen.width;
				viewHeight = Math.round(screen.width * window.innerHeight / window.innerWidth);
			}
		}

		// Super Hack
		// Limiting pixel ratio to two
		const pixelRatio = Math.min(2, window.devicePixelRatio);
		// Selecting whichever dimension that yields the best performance while not being too small
		viewWidth  = Math.min(window.innerWidth,  viewWidth)  * pixelRatio;
		viewHeight = Math.min(window.innerHeight, viewHeight) * pixelRatio;

		this._resize(viewWidth, viewHeight, window.innerWidth, window.innerHeight);
		this._setCanvasPosition(window.innerWidth, window.innerHeight);
		this._applyFullScreenStyles();
	}

	setFullScreen () {
		window.addEventListener('resize', this._setFullScreenWrapper);

		this.isFullScreen = true;
		this._setFullScreen();
		this.appendToDom(document.body);
	}

	private _setCanvasPosition (styleWidth: number, styleHeight: number) {
		const left = Math.floor((window.innerWidth - styleWidth) / 2);
		const top = Math.floor((window.innerHeight - styleHeight) / 2);
		this.canvas.style.left = left.toString() + 'px';
		this.canvas.style.top = top.toString() + 'px';
	}

	private _applyFullScreenStyles () {
		document.body.style.margin = '0';
		document.body.style.overflow = 'hidden';
		document.body.style.backgroundColor = '#000';
	}

	onResize (callback: (...args: any[]) => any) {
		this._onResizeCallbacks.push(callback);
	}

	removeOnResize (callback: (...args: any[]) => any) {
		var idx = this._onResizeCallbacks.indexOf(callback);
		if (idx !== -1) {
			this._onResizeCallbacks.splice(idx, 1);
		}
	}

};

export default WebGLView;
